import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { WebsiteBannerConfig } from './WebsiteBanner.types';
import { WebsiteBanner } from './WebsiteBanner';
import { WEBSITE_BANNER_HEIGHT_MOBILE, WEBSITE_BANNER_HEIGHT_DESKTOP } from './WebsiteBanner.constants';

interface WebsiteBannerContextValue
  extends Readonly<{
    registerBanner: (config: WebsiteBannerConfig) => void;
    unregisterBanner: (id: string) => void;
    bannerOffset: number;
    hasActiveBanner: boolean;
  }> {}

const WebsiteBannerContext = createContext<WebsiteBannerContextValue | undefined>(undefined);

// Export context for use in useWebsiteBannerOffset hook
export { WebsiteBannerContext };

export const WebsiteBannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<Map<string, WebsiteBannerConfig>>(new Map());
  const isMobile = useIsMobileScreen();

  const registerBanner = useEventCallback((config: WebsiteBannerConfig) => {
    setBanners(prev => {
      const next = new Map(prev);
      next.set(config.id, config);
      return next;
    });
  });

  const unregisterBanner = useEventCallback((id: string) => {
    setBanners(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  });

  // Elect the visible banner with the lowest `priority` (stack behavior - one at a time).
  // See `WebsiteBannerConfig.priority` for why the field is explicit rather than positional.
  const activeBanner = useMemo(() => {
    const visibleBanners = Array.from(banners.values()).filter(banner => banner.isVisible);
    return visibleBanners.sort((a, b) => a.priority - b.priority)[0] || null;
  }, [banners]);

  const hasActiveBanner = !!activeBanner;

  // Calculate banner offset based on whether a banner is active
  const bannerOffset = useMemo(() => {
    if (!hasActiveBanner) {
      return 0;
    }
    return isMobile ? WEBSITE_BANNER_HEIGHT_MOBILE : WEBSITE_BANNER_HEIGHT_DESKTOP;
  }, [hasActiveBanner, isMobile]);

  const contextValue = useMemo(
    () => ({
      registerBanner,
      unregisterBanner,
      bannerOffset,
      hasActiveBanner,
    }),
    [registerBanner, unregisterBanner, bannerOffset, hasActiveBanner],
  );

  return (
    <WebsiteBannerContext.Provider value={contextValue}>
      {activeBanner && (
        <WebsiteBanner
          message={activeBanner.message}
          icon={activeBanner.icon}
          actionButton={activeBanner.actionButton}
          onClose={activeBanner.onClose} // Pass through from config - see WebsiteBannerConfig docs for design guidelines
          closeButtonPlacement={activeBanner.closeButtonPlacement}
        />
      )}
      {children}
    </WebsiteBannerContext.Provider>
  );
};

/**
 * Hook to register a website banner in the global banner stack.
 *
 * **Throws without a `WebsiteBannerProvider` ancestor.** The provider owns the bar, so registering
 * with nowhere to render is a programming error rather than something to degrade quietly.
 *
 * Only one banner is shown at a time (stack behavior): the visible banner with the lowest
 * `priority` wins — see `WEBSITE_BANNER_PRIORITY` for the full ordering. When that banner is
 * dismissed or becomes invisible, the next visible banner automatically shows.
 *
 * @param config - Banner configuration including priority, visibility, message, action button, and optional close handler
 *
 * @example Basic banner without close button (non-dismissible)
 * ```tsx
 * useWebsiteBanner({
 *   id: 'critical-action',
 *   priority: WEBSITE_BANNER_PRIORITY.unpublishedWebsite,
 *   isVisible: needsAction,
 *   message: {
 *     default: 'Action required before continuing',
 *   },
 *   actionButton: {
 *     label: { default: 'Take Action' },
 *     onClick: handleAction,
 *   },
 *   // No onClose - user must take action via button
 * });
 * ```
 *
 * @example Dismissible banner with temporary state (session-only)
 * ```tsx
 * const [showBanner, setShowBanner] = useState(true);
 *
 * useWebsiteBanner({
 *   id: 'temporary-notice',
 *   priority: WEBSITE_BANNER_PRIORITY.changesReminder,
 *   isVisible: showBanner && someCondition,
 *   message: {
 *     default: 'This is a temporary notice',
 *   },
 *   onClose: () => setShowBanner(false), // Hides for current session only
 * });
 * ```
 *
 * @example Dismissible banner with persistent state (cross-session)
 * ```tsx
 * const { getUserConsent, setUserConsent } = useUserConsent();
 * const hasSeenBanner = getUserConsent('myBannerKey');
 *
 * useWebsiteBanner({
 *   id: 'persistent-notice',
 *   priority: WEBSITE_BANNER_PRIORITY.accommodationsVisibility,
 *   isVisible: !hasSeenBanner && someCondition,
 *   message: {
 *     default: 'Important one-time message',
 *     mobile: 'Important message',
 *   },
 *   actionButton: {
 *     label: { default: 'Learn More' },
 *     onClick: handleLearnMore,
 *   },
 *   onClose: () => setUserConsent('myBannerKey', true), // Persists across sessions
 * });
 * ```
 *
 * @example Banner with telemetry tracking on dismissal
 * ```tsx
 * useWebsiteBanner({
 *   id: 'tracked-banner',
 *   priority: WEBSITE_BANNER_PRIORITY.inAppBrowser,
 *   isVisible: showBanner,
 *   message: { default: 'Tracked message' },
 *   onClose: () => {
 *     telemetry.bannerDismissed('tracked-banner');
 *     setUserConsent('hasSeenTrackedBanner', true);
 *   },
 * });
 * ```
 *
 * See WebsiteBannerConfig interface for detailed guidelines on when to make
 * banners dismissible vs. non-dismissible.
 */
export const useWebsiteBanner = (config: WebsiteBannerConfig) => {
  const context = useContext(WebsiteBannerContext);

  if (!context) {
    throw new Error('useWebsiteBanner must be used within WebsiteBannerProvider');
  }

  const { registerBanner, unregisterBanner } = context;

  // `config` is a fresh object literal on every render of every caller, so this effect re-runs
  // constantly — cleanup unregisters, then the effect re-registers, two `setBanners` calls each
  // time. Skipping banners that are not visible is what keeps that bounded: they are filtered out
  // before election anyway, so registering one only churns provider state. Most visitors have no
  // visible banner at all, and now pay nothing for the ones they cannot see.
  useEffect(() => {
    if (!config.isVisible) {
      return;
    }

    registerBanner(config);

    return () => {
      unregisterBanner(config.id);
    };
  }, [config, registerBanner, unregisterBanner]);
};
